import React, {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";

const REEL_HEIGHT = 69;
const ANIMATION_DURATION = 10000;
const INITIAL_SPEED = 10;

const RouletteOfFaith = ({
	event,
	registerList = [],
	prizes = [],
	initialWinners = [],
	isRegisteredOnly = false,

	// Replace these with your Redux/API functions if preferred
	savePrizeWinner,
	saveWaivedWinner,
	getPrize,
}) => {
	const reelRef = useRef(null);
	const animationRef = useRef(null);
	const startTimeRef = useRef(null);
	const speedRef = useRef(INITIAL_SPEED);
	const winnerOffsetRef = useRef(null);

	const winnersPanelRef = useRef(null);
	const prizesPanelRef = useRef(null);
	const nextPrizePanelRef = useRef(null);

	const [participants, setParticipants] = useState(registerList);
	const [remainingPrizes, setRemainingPrizes] = useState(prizes);
	const [winners, setWinners] = useState(initialWinners);

	const [winner, setWinner] = useState(null);
	const [rolling, setRolling] = useState(false);
	const [canSaveWinner, setCanSaveWinner] = useState(false);
	const [handleActive, setHandleActive] = useState(false);

	const [visiblePanel, setVisiblePanel] = useState(null);
	const [guideVisible, setGuideVisible] = useState(false);
	const [nextPrize, setNextPrize] = useState(null);

	const [confetti, setConfetti] = useState([]);

	/*
	|--------------------------------------------------------------------------
	| Participant normalization
	|--------------------------------------------------------------------------
	*/

	const normalizedParticipants = useMemo(() => {
		return participants.map((participant, index) => {
			let reelOffset;

			/*
			 * Preserve the same offset logic as the Blade implementation.
			 *
			 * x === 0 => (length - 2) * 69
			 * x === 1 => (length - 1) * 69
			 * otherwise => (x - 2) * 69
			 */
			if (index === 0) {
				reelOffset = (participants.length - 2) * REEL_HEIGHT;
			} else if (index === 1) {
				reelOffset = (participants.length - 1) * REEL_HEIGHT;
			} else {
				reelOffset = (index - 2) * REEL_HEIGHT;
			}

			return {
				...participant,

				id: isRegisteredOnly
					? participant.rpa_user_id
					: participant.employee_id,

				name: isRegisteredOnly
					? participant.rpa_name
					: `${participant.first_name ?? ""} ${
							participant.last_name ?? ""
					  }`.trim(),

				reelOffset,
			};
		});
	}, [participants, isRegisteredOnly]);

	/*
	|--------------------------------------------------------------------------
	| Prizes
	|--------------------------------------------------------------------------
	*/

	const groupedPrizes = useMemo(() => {
		const grouped = {};

		remainingPrizes.forEach((prize) => {
			const name = prize.rep_name;

			if (!grouped[name]) {
				grouped[name] = {
					name,
					count: 0,
				};
			}

			grouped[name].count++;
		});

		return Object.values(grouped);
	}, [remainingPrizes]);

	/*
	|--------------------------------------------------------------------------
	| Confetti
	|--------------------------------------------------------------------------
	*/

	const createConfetti = useCallback(() => {
		const particles = Array.from({ length: 100 }, (_, index) => ({
			id: `${Date.now()}-${index}`,
			x: Math.random() * 500 - 250,
			y: Math.random() * 200 - 150,
			rotation: Math.random() * 360,
			hue: Math.random() * 360,
		}));

		setConfetti(particles);

		setTimeout(() => {
			setConfetti([]);
		}, 4000);
	}, []);

	/*
	|--------------------------------------------------------------------------
	| Winner
	|--------------------------------------------------------------------------
	*/

	const finishRoll = useCallback(
		(winnerOffset) => {
			const selectedParticipant = normalizedParticipants.find(
				(participant) =>
					participant.reelOffset === winnerOffset
			);

			setWinner(selectedParticipant ?? null);
			setRolling(false);
			setCanSaveWinner(true);

			startTimeRef.current = null;

			createConfetti();
		},
		[normalizedParticipants, createConfetti]
	);

	/*
	|--------------------------------------------------------------------------
	| Animation
	|--------------------------------------------------------------------------
	*/

	const animate = useCallback(
		(timestamp) => {
			if (!reelRef.current) {
				return;
			}

			if (startTimeRef.current === null) {
				startTimeRef.current = timestamp;
			}

			const elapsed =
				timestamp - startTimeRef.current;

			const reelHeight =
				reelRef.current.firstElementChild
					?.scrollHeight ?? 0;

			if (!reelHeight) {
				return;
			}

			const remaining =
				ANIMATION_DURATION - elapsed;

			const scrollTop =
				(
					(speedRef.current /
						ANIMATION_DURATION) *
						remaining *
						remaining +
					winnerOffsetRef.current
				) % reelHeight;

			reelRef.current.scrollTop =
				Math.floor(scrollTop);

			speedRef.current -=
				speedRef.current * 0.01;

			if (elapsed < ANIMATION_DURATION) {
				animationRef.current =
					requestAnimationFrame(animate);

				return;
			}

			reelRef.current.scrollTop =
				winnerOffsetRef.current;

			finishRoll(
				winnerOffsetRef.current
			);
		},
		[finishRoll]
	);

	/*
	|--------------------------------------------------------------------------
	| Roll
	|--------------------------------------------------------------------------
	*/

	const roll = useCallback(() => {
		if (rolling) {
			return;
		}

		if (!remainingPrizes.length) {
			setNextPrize(null);
			setVisiblePanel("nextPrize");
			return;
		}

		if (normalizedParticipants.length < 2) {
			return;
		}

		setWinner(null);
		setCanSaveWinner(false);
		setRolling(true);

		setHandleActive(true);

		setTimeout(() => {
			setHandleActive(false);
		}, 1000);

		speedRef.current =
			Math.random() + INITIAL_SPEED;

		/*
		 * Same winner generation logic as Blade:
		 *
		 * (Math.floor(Math.random() * (reels.length - 1)) + 1)
		 * * reelHeight
		 */
		const winnerOffset =
			(
				Math.floor(
					Math.random() *
						(normalizedParticipants.length - 1)
				) + 1
			) * REEL_HEIGHT;

		winnerOffsetRef.current =
			winnerOffset;

		startTimeRef.current = null;

		animationRef.current =
			requestAnimationFrame(animate);
	}, [
		rolling,
		remainingPrizes.length,
		normalizedParticipants.length,
		animate,
	]);

	/*
	|--------------------------------------------------------------------------
	| Remove participant after winning/waiving
	|--------------------------------------------------------------------------
	*/

	const removeCurrentWinner = useCallback(() => {
		if (!winner) {
			return;
		}

		setParticipants((current) =>
			current.filter((participant) => {
				const participantId =
					participant.rpa_user_id ??
					participant.employee_id;

				return (
					String(participantId) !==
					String(winner.id)
				);
			})
		);

		setWinner(null);
		setCanSaveWinner(false);
	}, [winner]);

	/*
	|--------------------------------------------------------------------------
	| Save Winner
	|--------------------------------------------------------------------------
	*/

	const addToWinnerList = useCallback(async () => {
		if (
			!remainingPrizes.length ||
			!winner ||
			!canSaveWinner
		) {
			return;
		}

		setCanSaveWinner(false);

		const prize =
			remainingPrizes[0];

		try {
			await savePrizeWinner?.(
				prize.rep_id,
				{
					winner_name: winner.name,
					winner_id: winner.id,
				}
			);

			setWinners((current) => [
				...current,
				{
					...prize,
					rep_winner_name:
						winner.name,
					winner_id: winner.id,
				},
			]);

			setRemainingPrizes(
				(current) => current.slice(1)
			);

			removeCurrentWinner();
		} catch (error) {
			console.error(
				"Unable to save winner:",
				error
			);

			setCanSaveWinner(true);
		}
	}, [
		remainingPrizes,
		winner,
		canSaveWinner,
		savePrizeWinner,
		removeCurrentWinner,
	]);

	/*
	|--------------------------------------------------------------------------
	| Waive
	|--------------------------------------------------------------------------
	*/

	const waiveWinner = useCallback(async () => {
		if (!winner || !canSaveWinner) {
			return;
		}

		setCanSaveWinner(false);

		try {
			await saveWaivedWinner?.(
				winner.id,
				{
					event_id: event?.rev_id,
					winner_id: winner.id,
				}
			);

			removeCurrentWinner();
		} catch (error) {
			console.error(
				"Unable to waive winner:",
				error
			);

			setCanSaveWinner(true);
		}
	}, [
		winner,
		canSaveWinner,
		saveWaivedWinner,
		event?.rev_id,
		removeCurrentWinner,
	]);

	/*
	|--------------------------------------------------------------------------
	| Panels
	|--------------------------------------------------------------------------
	*/

	const showWinnersPanel =
		useCallback(() => {
			setVisiblePanel("winners");
		}, []);

	const showPrizePanel =
		useCallback(() => {
			setVisiblePanel("prizes");
		}, []);

	const showNextPrizePanel =
		useCallback(async () => {
			setVisiblePanel("nextPrize");

			const prize =
				remainingPrizes[0];

			if (!prize) {
				setNextPrize(null);
				return;
			}

			try {
				if (getPrize) {
					const response =
						await getPrize(
							prize.rep_id
						);

					setNextPrize(
						response?.data ??
							response
					);
				} else {
					setNextPrize(prize);
				}
			} catch (error) {
				console.error(
					"Unable to load prize:",
					error
				);
			}
		}, [remainingPrizes, getPrize]);

	const hidePanels = useCallback(() => {
		setVisiblePanel(null);
	}, []);

	/*
	|--------------------------------------------------------------------------
	| Keyboard scrolling
	|--------------------------------------------------------------------------
	*/

	const scrollCurrentPanel =
		useCallback(
			(direction) => {
				let panel = null;

				if (
					visiblePanel === "winners"
				) {
					panel =
						winnersPanelRef.current;
				}

				if (
					visiblePanel === "prizes"
				) {
					panel =
						prizesPanelRef.current;
				}

				if (
					visiblePanel ===
					"nextPrize"
				) {
					panel =
						nextPrizePanelRef.current;
				}

				if (!panel) {
					return;
				}

				panel.scrollBy({
					top:
						direction === "down"
							? REEL_HEIGHT
							: -REEL_HEIGHT,
					behavior: "smooth",
				});
			},
			[visiblePanel]
		);

	/*
	|--------------------------------------------------------------------------
	| Keyboard Controls
	|--------------------------------------------------------------------------
	*/

	useEffect(() => {
		const handleKeyDown = (event) => {
			const key =
				event.key.toLowerCase();

			switch (key) {
				case "r":
					roll();
					break;

				case "a":
					addToWinnerList();
					break;

				case "w":
					waiveWinner();
					break;

				case "s":
					showWinnersPanel();
					break;

				case "n":
					showNextPrizePanel();
					break;

				case "p":
					showPrizePanel();
					break;

				case "x":
					hidePanels();
					break;

				case "g":
					setGuideVisible(
						(current) =>
							!current
					);
					break;

				case "2":
					scrollCurrentPanel(
						"down"
					);
					break;

				case "8":
					scrollCurrentPanel(
						"up"
					);
					break;

				default:
					break;
			}
		};

		document.addEventListener(
			"keydown",
			handleKeyDown
		);

		return () => {
			document.removeEventListener(
				"keydown",
				handleKeyDown
			);
		};
	}, [
		roll,
		addToWinnerList,
		waiveWinner,
		showWinnersPanel,
		showNextPrizePanel,
		showPrizePanel,
		hidePanels,
		scrollCurrentPanel,
	]);

	/*
	|--------------------------------------------------------------------------
	| Cleanup animation
	|--------------------------------------------------------------------------
	*/

	useEffect(() => {
		return () => {
			if (animationRef.current) {
				cancelAnimationFrame(
					animationRef.current
				);
			}
		};
	}, []);

	const renderParticipants = (type) =>
		normalizedParticipants.map(
			(participant, index) => {
				const isWinner =
					winner?.id ===
					participant.id;

				return (
					<p
						key={`${type}-${participant.id}-${index}`}
						className={`participant ${
							isWinner
								? "winner"
								: ""
						}`}
						data-reel-id={
							participant.reelOffset
						}
						data-participant-id={
							participant.id
						}
					>
						{participant.name}
					</p>
				);
			}
		);

	return (
		<div
			className="raffle-roulette"
			style={{
				backgroundImage: `url(${
					event?.rev_bg_url ||
					"/images/SLM_2_3.jpg"
				})`,
			}}
		>
			<div id="roulette_of_faith">
				<div className="group-roulette">
					<div
						ref={reelRef}
						className="reel"
					>
						<div className="actual">
							{renderParticipants(
								"actual"
							)}
						</div>

						<div className="clone">
							{renderParticipants(
								"clone"
							)}
						</div>
					</div>
				</div>

				<button
					type="button"
					aria-label="Roll roulette"
					className={`handle lever ${
						handleActive
							? "active"
							: ""
					}`}
					onClick={roll}
				/>

				{confetti.map((particle) => (
					<i
						key={particle.id}
						className="confettiParticles"
						style={{
							transform: `translate3d(${particle.x}px, ${particle.y}px, 0) rotate(${particle.rotation}deg)`,
							background: `hsla(${particle.hue}, 100%, 50%, 1)`,
							animation:
								"bang 700ms ease-out forwards",
							animationIterationCount:
								5,
							opacity: 0,
						}}
					/>
				))}
			</div>

			{/* Prize Winners */}
			<div
				className={`prize-winners leaderboard ${
					visiblePanel === "winners"
						? "show"
						: ""
				}`}
			>
				<div className="card">
					<div
						ref={winnersPanelRef}
						className="card-body"
					>
						<h1>
							Prize Winners
						</h1>

						{winners.length ? (
							winners.map(
								(
									item,
									index
								) => (
									<div
										key={`${item.rep_id}-${index}`}
										className="prize-item"
									>
										<h2 className="prize-name">
											{
												item.rep_name
											}
										</h2>

										<h4 className="prize-winner">
											{
												item.rep_winner_name
											}
										</h4>
									</div>
								)
							)
						) : (
							<div className="prize-item">
								<h2 className="prize-name">
									No Winners
									Yet.
								</h2>

								<h4 className="prize-winner">
									Let's roll
									the
									roulette!!!
								</h4>
							</div>
						)}
					</div>
				</div>
			</div>

			{/* Remaining prizes */}
			<div
				className={`prize-display leaderboard ${
					visiblePanel === "prizes"
						? "show"
						: ""
				}`}
			>
				<div className="card">
					<div
						ref={prizesPanelRef}
						className="card-body"
					>
						<h1>Prizes</h1>

						{groupedPrizes.length ? (
							groupedPrizes.map(
								(prize) => (
									<div
										key={
											prize.name
										}
										className="prize-item"
									>
										<h2 className="prize-name">
											(
											{
												prize.count
											}
											){" "}
											{
												prize.name
											}
										</h2>
									</div>
								)
							)
						) : (
							<div className="prize-item">
								<h2 className="prize-name">
									No more
									prizes
									left.
								</h2>
							</div>
						)}
					</div>
				</div>
			</div>

			{/* Next Prize */}
			<div
				className={`next-prize-display leaderboard ${
					visiblePanel ===
					"nextPrize"
						? "show"
						: ""
				}`}
			>
				<div className="card">
					<div
						ref={
							nextPrizePanelRef
						}
						className="card-body"
					>
						<h1>Next Prize</h1>

						<div className="prize-item">
							<h2 className="prize-name">
								{remainingPrizes.length
									? nextPrize?.rep_name ??
									  remainingPrizes[0]
											?.rep_name ??
									  "Loading Next Prize"
									: "No more prizes left."}
							</h2>
						</div>
					</div>
				</div>
			</div>

			{/* Keyboard Guide */}
			<div
				className={`control-guide ${
					guideVisible
						? "show"
						: ""
				}`}
			>
				<div className="card">
					<div className="card-body">
						<h3>
							Control Keys
						</h3>

						<ul>
							<li>
								g - toggle
								guide
							</li>
							<li>
								r - roll
							</li>
							<li>
								a - save
								winner
							</li>
							<li>
								w - waive
								winner
							</li>
							<li>
								s - winners
							</li>
							<li>
								n - next
								prize
							</li>
							<li>
								p - prizes
							</li>
							<li>
								x - close
								panel
							</li>
							<li>
								2 - scroll
								down
							</li>
							<li>
								8 - scroll
								up
							</li>
						</ul>
					</div>
				</div>
			</div>
		</div>
	);
};

export default RouletteOfFaith;